/**
 * HousingBookPublic — public booking marketplace for housing rooms / dorms.
 *
 * Mounted at /housing/book. Lists every room with is_public=true, lets
 * external guests pick check-in/out dates, fills contact info, submits.
 * The housing team approves/cancels from /housing → Bookings tab.
 */
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Home, CalendarCheck, Users, ArrowLeft, Loader2, CheckCircle2, Search, DollarSign, Moon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n, c = 'USD') => `${c} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const HousingBookPublic = () => {
  const [search, setSearch] = useState('');
  const [bookingFor, setBookingFor] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const { data: rooms = [], isLoading } = useQuery({
    queryKey: ['housing-public-rooms'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/housing/public/rooms`);
      if (!res.ok) throw new Error('Failed to load');
      return res.json();
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rooms;
    return rooms.filter(r => (r.name + ' ' + (r.building || '') + ' ' + (r.description || '')).toLowerCase().includes(q));
  }, [rooms, search]);

  if (confirmation) return <ConfirmationView confirmation={confirmation} onReset={() => { setConfirmation(null); setBookingFor(null); }} />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="housing-public">
      <header className="bg-gradient-to-br from-teal-900 via-emerald-800 to-teal-700 text-white">
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          <div className="flex items-center gap-3 mb-4">
            <Home className="h-7 w-7 text-teal-200" />
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-teal-200">Housing</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">Find a room for your stay</h1>
          <p className="mt-3 text-sm sm:text-base text-teal-100 max-w-xl">
            Dorms, guest rooms, and event accommodation. Pick your dates, tell us a bit about you, and we'll confirm within 24 hours.
          </p>
          <div className="mt-6 max-w-md relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-teal-200" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search rooms, buildings…" className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-teal-200 focus:bg-white/20" data-testid="housing-public-search" />
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading rooms…</div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-slate-500"><Home className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            {rooms.length === 0
              ? <p>No rooms are open for public booking right now.</p>
              : <p>No rooms match "{search}".</p>}
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map(r => (
              <Card key={r.id} className="hover:shadow-lg transition-shadow overflow-hidden" data-testid={`housing-public-room-${r.id}`}>
                {r.photos[0] ? (
                  <div className="h-40 bg-cover bg-center" style={{ backgroundImage: `url(${r.photos[0]})` }} />
                ) : (
                  <div className="h-40 bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center">
                    <Home className="h-12 w-12 text-teal-400" />
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Home className="h-4 w-4 text-teal-600" />
                        {r.name}
                      </CardTitle>
                      <Badge variant="outline" className="text-[10px] mt-1">{r.building || 'Main building'}</Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-teal-700">{r.nightly_rate > 0 ? fmtMoney(r.nightly_rate, r.currency) : 'Free'}</p>
                      <p className="text-[10px] text-slate-500">per night</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {r.description && <p className="text-xs text-slate-600 mb-3 line-clamp-2">{r.description}</p>}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 flex items-center gap-1"><Users className="h-3 w-3" />Sleeps {r.capacity}</span>
                    <Button size="sm" onClick={() => setBookingFor(r)} data-testid={`housing-public-book-${r.id}`}><CalendarCheck className="h-3.5 w-3.5 mr-1" />Book</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
      {bookingFor && <BookingDialog room={bookingFor} onClose={() => setBookingFor(null)} onConfirmed={c => setConfirmation(c)} />}
    </div>
  );
};


function BookingDialog({ room, onClose, onConfirmed }) {
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400_000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    check_in_date: today, check_out_date: tomorrow,
    client_name: '', client_email: '', client_phone: '',
    guests_count: 1, purpose: '', notes: '',
  });
  const nights = useMemo(() => {
    try {
      const a = new Date(form.check_in_date).getTime();
      const b = new Date(form.check_out_date).getTime();
      return Math.max(0, Math.ceil((b - a) / 86400_000));
    } catch { return 0; }
  }, [form.check_in_date, form.check_out_date]);
  const estimated = +(nights * (room.nightly_rate || 0)).toFixed(2);

  const { data: availability } = useQuery({
    queryKey: ['housing-public-avail', room.id, form.check_in_date, form.check_out_date],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/housing/public/rooms/${room.id}/availability?from=${form.check_in_date}&to=${form.check_out_date}`);
      if (!res.ok) return { blocked: [] };
      return res.json();
    },
    enabled: !!room.id && !!form.check_in_date && !!form.check_out_date,
  });

  const submit = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/housing/public/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_id: room.id,
          check_in_date: form.check_in_date,
          check_out_date: form.check_out_date,
          client_name: form.client_name,
          client_email: form.client_email,
          client_phone: form.client_phone,
          guests_count: parseInt(form.guests_count, 10) || 1,
          purpose: form.purpose,
          notes: form.notes,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = typeof data.detail === 'object' ? (data.detail.message || JSON.stringify(data.detail)) : (data.detail || 'Booking failed');
        throw new Error(detail);
      }
      return data;
    },
    onSuccess: data => { toast.success('Booking request submitted!'); onConfirmed?.(data); },
    onError: e => toast.error(e.message),
  });

  const valid = form.client_name && form.client_email && nights > 0 && form.guests_count > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="housing-public-book-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-teal-600" />Book {room.name}</DialogTitle>
          <DialogDescription>{room.building || 'Main building'} · sleeps {room.capacity}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Check in *</Label><Input type="date" min={today} value={form.check_in_date} onChange={e => setForm({ ...form, check_in_date: e.target.value })} data-testid="housing-public-checkin" /></div>
            <div><Label>Check out *</Label><Input type="date" min={form.check_in_date} value={form.check_out_date} onChange={e => setForm({ ...form, check_out_date: e.target.value })} data-testid="housing-public-checkout" /></div>
          </div>
          {availability?.blocked?.length > 0 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              <p className="font-medium mb-0.5">Already booked in this window:</p>
              <ul className="space-y-0.5">
                {availability.blocked.slice(0, 5).map((b, i) => (
                  <li key={i}>· {b.check_in_date} → {b.check_out_date}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="rounded-lg bg-teal-50 p-3 flex items-center justify-between">
            <div className="text-xs text-teal-900 flex items-center gap-1">
              <Moon className="h-3 w-3 mr-1" />{nights} night{nights === 1 ? '' : 's'}
              {room.nightly_rate > 0 && <><span className="mx-1">·</span><DollarSign className="h-3 w-3" />{fmtMoney(room.nightly_rate, room.currency)}/night</>}
            </div>
            <div className="text-lg font-bold text-teal-800">{room.nightly_rate > 0 ? fmtMoney(estimated, room.currency) : 'Free'}</div>
          </div>
          <div className="grid grid-cols-2 gap-2 border-t pt-3">
            <div className="col-span-2"><Label>Your name *</Label><Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} placeholder="Jane Doe" data-testid="housing-public-name" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} placeholder="jane@example.com" data-testid="housing-public-email" /></div>
            <div><Label>Phone</Label><Input value={form.client_phone} onChange={e => setForm({ ...form, client_phone: e.target.value })} /></div>
            <div><Label>Guests *</Label><Input type="number" min={1} max={room.capacity} value={form.guests_count} onChange={e => setForm({ ...form, guests_count: e.target.value })} data-testid="housing-public-guests" /></div>
            <div className="col-span-2"><Label>Purpose</Label><Input value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Retreat attendee" data-testid="housing-public-purpose" /></div>
            <div className="col-span-2"><Label>Anything else?</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Accessibility needs, arrival time…" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => submit.mutate()} disabled={!valid || submit.isPending} data-testid="housing-public-submit">
            {submit.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ConfirmationView({ confirmation, onReset }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="housing-public-confirmation">
      <Card className="max-w-md w-full text-center">
        <CardContent className="pt-10 pb-8 space-y-4">
          <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-8 w-8 text-teal-700" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">Request received!</h2>
          <p className="text-sm text-slate-600">{confirmation.message}</p>
          <div className="bg-slate-50 rounded-lg p-4 text-left text-sm space-y-1">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Booking reference</p>
            <p className="text-base font-mono font-bold text-teal-700" data-testid="housing-public-confirmation-ref">{confirmation.booking_reference}</p>
            <p className="mt-2"><b>Room:</b> {confirmation.room_name}</p>
            <p><b>Dates:</b> {confirmation.check_in_date} → {confirmation.check_out_date} ({confirmation.nights} night{confirmation.nights === 1 ? '' : 's'})</p>
            {confirmation.estimated_cost > 0 && <p><b>Estimated:</b> {fmtMoney(confirmation.estimated_cost, confirmation.currency)}</p>}
          </div>
          <p className="text-xs text-slate-400">Save this reference — we'll email you with the confirmation.</p>
          <Button variant="outline" onClick={onReset} className="mt-2"><ArrowLeft className="h-3.5 w-3.5 mr-1" />Browse more rooms</Button>
        </CardContent>
      </Card>
    </div>
  );
}


export default HousingBookPublic;
